import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    Upload,
    ShieldCheck,
    ArrowLeft,
    Trash2,
    RotateCcw,
    Send,
    Loader2,
    CheckCircle2,
    Star,
} from "lucide-react";

/**
 * Route: /settings/verify-vet
 *
 * Veteran verification flow:
 *   1. Upload a DD-214 or service record (PDF or image)
 *   2. Draw black rectangles over anything you want private (SSN, DOB, etc.)
 *   3. Submit for admin review → on approval, 50% off Atlas forever
 *
 * The canvas coordinate system used here is exactly what the backend
 * expects when compositing the redactions on the source image. Boxes can
 * be dragged, resized, deleted, and undone; the state is autosaved on
 * every mutation so a page refresh doesn't lose work.
 */
export default function VerifyVet() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);   // full status blob from GET /me/verification/status
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Redaction state
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
    const [boxes, setBoxes] = useState([]);         // {x, y, w, h}
    const [drawing, setDrawing] = useState(null);   // in-progress rectangle
    const [selected, setSelected] = useState(null); // index of the selected box
    const [dragMode, setDragMode] = useState(null); // 'move' | 'resize' | null
    const dragStart = useRef(null);
    // Blob URL of the authenticated canvas image
    const [canvasBlob, setCanvasBlob] = useState(null);

    // Load current status on mount
    useEffect(() => {
        api.get("/me/verification/status")
            .then(({ data }) => {
                setStatus(data);
                setBoxes(data.boxes || []);
            })
            .catch(() => {});
    }, []);

    // Fetch the canvas image as a blob (authenticated) — img src can't send headers
    useEffect(() => {
        const url = status?.canvas_url;
        if (!url || status.status === "approved") {
            if (canvasBlob) URL.revokeObjectURL(canvasBlob);
            setCanvasBlob(null);
            return;
        }
        let objectUrl = null;
        api.get(url.replace(/^\/api/, ""), { responseType: "blob" })
            .then(({ data }) => {
                objectUrl = URL.createObjectURL(data);
                setCanvasBlob(objectUrl);
            })
            .catch(() => toast.error("Couldn't load the uploaded file — try re-uploading."));
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line
    }, [status?.canvas_url, status?.status]);

    // Debounced autosave — 500ms after last mutation
    useEffect(() => {
        if (!status || status.status !== "draft") return;
        if (!imgSize.w || !imgSize.h) return;
        const t = setTimeout(() => {
            api.put("/me/verification/redactions", {
                boxes,
                canvas_width: imgSize.w,
                canvas_height: imgSize.h,
            }).catch(() => {});
        }, 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [boxes, imgSize.w, imgSize.h, status?.status]);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/me/verification/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const st = await api.get("/me/verification/status");
            setStatus(st.data);
            setBoxes(st.data.boxes || []);
            toast.success("File uploaded — now draw over anything private.");
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!boxes.length) {
            const ok = window.confirm(
                "No redactions drawn — submit as-is? You can always add more before an admin reviews."
            );
            if (!ok) return;
        }
        setSubmitting(true);
        try {
            await api.put("/me/verification/redactions", {
                boxes,
                canvas_width: imgSize.w,
                canvas_height: imgSize.h,
            });
            await api.post("/me/verification/submit");
            const st = await api.get("/me/verification/status");
            setStatus(st.data);
            toast.success("Submitted for review — we'll email you when it's approved.");
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    // Canvas mouse handlers ------------------------------------------------
    const canvasCoord = (e) => {
        const rect = imgRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const startDraw = (e) => {
        if (status?.status !== "draft") return;
        // If a box is hit, we drag/resize; else we start drawing a new one.
        const { x, y } = canvasCoord(e);
        const hit = boxes.findIndex((b) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h);
        if (hit >= 0) {
            const b = boxes[hit];
            // Right/bottom 12px is a resize handle
            const nearRight = x >= b.x + b.w - 14;
            const nearBottom = y >= b.y + b.h - 14;
            setSelected(hit);
            setDragMode(nearRight && nearBottom ? "resize" : "move");
            dragStart.current = { x, y, box: { ...b } };
            return;
        }
        setSelected(null);
        setDragMode(null);
        setDrawing({ x, y, w: 0, h: 0, startX: x, startY: y });
    };

    const moveDraw = (e) => {
        if (status?.status !== "draft") return;
        const { x, y } = canvasCoord(e);
        if (drawing) {
            setDrawing({
                x: Math.min(drawing.startX, x),
                y: Math.min(drawing.startY, y),
                w: Math.abs(x - drawing.startX),
                h: Math.abs(y - drawing.startY),
                startX: drawing.startX,
                startY: drawing.startY,
            });
            return;
        }
        if (dragMode && selected != null && dragStart.current) {
            const dx = x - dragStart.current.x;
            const dy = y - dragStart.current.y;
            setBoxes((prev) =>
                prev.map((b, i) => {
                    if (i !== selected) return b;
                    if (dragMode === "move") {
                        return { ...b, x: Math.max(0, dragStart.current.box.x + dx), y: Math.max(0, dragStart.current.box.y + dy) };
                    }
                    return { ...b, w: Math.max(8, dragStart.current.box.w + dx), h: Math.max(8, dragStart.current.box.h + dy) };
                })
            );
        }
    };

    const endDraw = () => {
        if (drawing && drawing.w > 4 && drawing.h > 4) {
            setBoxes((prev) => [...prev, { x: drawing.x, y: drawing.y, w: drawing.w, h: drawing.h }]);
        }
        setDrawing(null);
        setDragMode(null);
        dragStart.current = null;
    };

    const deleteSelected = () => {
        if (selected == null) return;
        setBoxes((prev) => prev.filter((_, i) => i !== selected));
        setSelected(null);
    };

    const clearAll = () => setBoxes([]);

    // Guard against undefined user
    if (!user) return null;

    return (
        <div className="min-h-screen bg-slate-50">
            <TopBar />
            <main className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-testid="verify-vet-page">
                <header className="mb-6">
                    <Link to="/settings" className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-900">
                        <ArrowLeft className="w-3 h-3" /> Back to settings
                    </Link>
                    <h1 className="mt-1 font-display font-black text-4xl tracking-tighter text-slate-900 inline-flex items-center gap-3">
                        <Star className="w-8 h-8 text-amber-600" /> Veteran Verification
                    </h1>
                    <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                        Upload your DD-214 or any service record. Redact anything you want
                        private — SSN, date of birth, home address, whatever. When our team
                        approves it, we apply a permanent 50% off your Atlas subscription.
                    </p>
                </header>

                <StatusBanner status={status} />

                {(!status || status.status === "none") && (
                    <UploadBox uploading={uploading} onFile={onFile} />
                )}

                {status && ["draft", "pending", "rejected"].includes(status.status) && (
                    <>
                        {status.status !== "draft" && (
                            <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
                                <div className="text-sm text-slate-700">
                                    Want to redo it? Uploading a new file will replace this one.
                                </div>
                                <label className="arcade-btn mt-3 cursor-pointer inline-flex" data-testid="verify-vet-reupload">
                                    <Upload className="w-4 h-4" />
                                    {uploading ? "Uploading…" : "Replace file"}
                                    <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onFile} />
                                </label>
                            </div>
                        )}
                        {canvasBlob && (
                            <>
                                <RedactionToolbar
                                    boxCount={boxes.length}
                                    onClear={clearAll}
                                    selectedIndex={selected}
                                    onDelete={deleteSelected}
                                    onSubmit={submit}
                                    submitting={submitting}
                                    disabled={status.status !== "draft"}
                                />
                                <div
                                    ref={containerRef}
                                    className="relative crystal rounded-3xl overflow-hidden select-none"
                                    style={{ cursor: status.status === "draft" ? "crosshair" : "default" }}
                                    data-testid="verify-vet-canvas"
                                >
                                    <img
                                        ref={imgRef}
                                        src={canvasBlob}
                                        alt="Uploaded service record"
                                        draggable={false}
                                        className="w-full h-auto block"
                                        onLoad={(e) => setImgSize({ w: e.currentTarget.clientWidth, h: e.currentTarget.clientHeight })}
                                        onMouseDown={startDraw}
                                        onMouseMove={moveDraw}
                                        onMouseUp={endDraw}
                                        onMouseLeave={endDraw}
                                        style={{ pointerEvents: status.status === "draft" ? "auto" : "none" }}
                                    />
                                    {boxes.map((b, i) => (
                                        <div
                                            key={i}
                                            className="absolute bg-black"
                                            style={{
                                                left: b.x, top: b.y, width: b.w, height: b.h,
                                                outline: i === selected ? "2px solid #FFC107" : "none",
                                                pointerEvents: "none",
                                            }}
                                            data-testid={`redact-box-${i}`}
                                        />
                                    ))}
                                    {drawing && (
                                        <div
                                            className="absolute bg-black/70 border border-white"
                                            style={{ left: drawing.x, top: drawing.y, width: drawing.w, height: drawing.h, pointerEvents: "none" }}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}

                {status?.status === "approved" && (
                    <div className="crystal rounded-3xl p-8 text-center" data-testid="verify-vet-approved">
                        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                        <h2 className="mt-3 font-display font-black text-2xl text-slate-900">
                            You're verified. Thank you for your service.
                        </h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Atlas is now 50% off on your account, forever. Your redacted document is on file.
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}

/* --------------------------------- UI parts -------------------------------- */

function StatusBanner({ status }) {
    if (!status || status.status === "none" || status.status === "draft") return null;
    const map = {
        pending: {
            bg: "bg-amber-50 border-amber-200 text-amber-900",
            title: "Under review",
            body: "Our team will verify your record and apply the 50% discount within 3 business days.",
        },
        rejected: {
            bg: "bg-rose-50 border-rose-200 text-rose-900",
            title: "Unable to verify",
            body: status.admin_notes || "Something wasn't clear enough — try uploading a cleaner or more complete document.",
        },
        approved: null,
    };
    const cfg = map[status.status];
    if (!cfg) return null;
    return (
        <div className={`rounded-2xl border px-5 py-4 mb-5 ${cfg.bg}`} data-testid={`verify-status-${status.status}`}>
            <div className="font-display font-bold">{cfg.title}</div>
            <div className="text-sm mt-1">{cfg.body}</div>
        </div>
    );
}

function UploadBox({ uploading, onFile }) {
    return (
        <label
            className="block crystal rounded-3xl border-2 border-dashed border-slate-300 p-10 text-center cursor-pointer hover:bg-slate-50 transition-colors"
            data-testid="verify-vet-upload"
        >
            <Upload className="w-10 h-10 text-slate-400 mx-auto" />
            <div className="mt-3 font-display font-bold text-slate-900">
                {uploading ? "Uploading…" : "Upload DD-214 or service record"}
            </div>
            <div className="text-sm text-slate-500 mt-1">
                PDF, PNG, JPG, or WebP · up to 10MB
            </div>
            <input
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                onChange={onFile}
                data-testid="verify-vet-upload-input"
            />
        </label>
    );
}

function RedactionToolbar({ boxCount, onClear, selectedIndex, onDelete, onSubmit, submitting, disabled }) {
    return (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="verify-vet-toolbar">
            <div className="text-sm text-slate-700">
                Draw over anything private. Boxes autosave.
                {boxCount > 0 && <span className="ml-2 font-bold text-slate-900">{boxCount} redaction{boxCount === 1 ? "" : "s"}</span>}
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={onDelete}
                    disabled={selectedIndex == null || disabled}
                    className="arcade-btn text-xs"
                    data-testid="verify-vet-delete-selected"
                >
                    <Trash2 className="w-3.5 h-3.5" /> Delete selected
                </button>
                <button
                    type="button"
                    onClick={onClear}
                    disabled={boxCount === 0 || disabled}
                    className="arcade-btn text-xs"
                    data-testid="verify-vet-clear-all"
                >
                    <RotateCcw className="w-3.5 h-3.5" /> Clear all
                </button>
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={submitting || disabled}
                    className="arcade-btn"
                    data-variant="primary"
                    data-testid="verify-vet-submit"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Submit for review
                </button>
            </div>
        </div>
    );
}
